// One-on-One Coach (Aria) process handlers. The live branch calls the cloud functions; the mock branch
// returns believable local data (see ./mocks/coachMocks) so the page works offline and in mock mode.

import type {
  AppendAssistantTurnsRequest,
  AppendAssistantTurnsResponse,
  AppendCoachVoiceTurnsRequest,
  AppendCoachVoiceTurnsResponse,
  DeleteUserGoalRequest,
  DeleteUserGoalResponse,
  UpdateUserGoalRequest,
  UpdateUserGoalResponse,
  EndCoachConversationRequest,
  EndCoachConversationResponse,
  GetCoachConversationRequest,
  GetCoachConversationResponse,
  GetCoachConversationsResponse,
  GetCoachStatusResponse,
  GetUserGoalsRequest,
  GetUserGoalsResponse,
  MintCoachLiveTokenRequest,
  MintCoachLiveTokenResponse,
  RememberForLaterRequest,
  RememberForLaterResponse,
  RequestGoalSettingRequest,
  RequestGoalSettingResponse,
  SaveUserGoalsRequest,
  SaveUserGoalsResponse,
  SendCoachMessageRequest,
  SendCoachMessageResponse,
  StartCoachConversationRequest,
  StartCoachConversationResponse
} from '../../../../pure-js/types/CoachApi'
import { CoachRole, GoalSource } from '../../../../pure-js/types/CoachTypes'
import { createUniqueId } from '../../../../pure-js/libs/Common'
import { loadCloudFunctionsHandler } from '../handlers'
import { handleRequest } from './config'
import {
  MOCK_COACH_CONVERSATIONS,
  MOCK_COACH_GOALS,
  MOCK_OVERALL_SUMMARY,
  buildMockConversation,
  buildMockEditedGoals,
  buildMockEndedConversation,
  buildMockGreeting,
  buildMockReply,
  buildMockLiveTokenConfig,
  buildMockSavedGoals,
  buildMockStartedConversation,
  buildMockThread
} from './mocks/coachMocks'

export const getCoachStatus = async (): Promise<GetCoachStatusResponse> =>
  handleRequest<GetCoachStatusResponse>(
    async () => ({
      success: true,
      data: {
        enabled: true,
        role: CoachRole.GAMER,
        onboardingGoalsCompleted: true,
        hasPlayedGame: true,
        postGamePending: true,
        latestSessionId: 'mock-session',
        assistantCoachingEnabled: true,
        // Screenshot/dev override so the disabled-org and post-game states can be shown too.
        ...(typeof window !== 'undefined' ? (window as { __coachStatusMock?: object }).__coachStatusMock : undefined)
      }
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getCoachStatus()
    }
  )

export const getCoachConversations = async (): Promise<GetCoachConversationsResponse> =>
  handleRequest<GetCoachConversationsResponse>(
    async () => ({
      success: true,
      data: { overallSummary: MOCK_OVERALL_SUMMARY, conversations: MOCK_COACH_CONVERSATIONS }
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getCoachConversations()
    }
  )

export const getCoachConversation = async (
  request: GetCoachConversationRequest
): Promise<GetCoachConversationResponse> =>
  handleRequest<GetCoachConversationResponse>(
    async () => ({
      success: true,
      data: {
        conversation: buildMockConversation(request.conversationId),
        messages: buildMockThread(request.conversationId)
      }
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getCoachConversation(request)
    }
  )

export const startCoachConversation = async (
  request: StartCoachConversationRequest
): Promise<StartCoachConversationResponse> =>
  handleRequest<StartCoachConversationResponse>(
    async () => {
      const conversation = buildMockStartedConversation({ flow: request.flow, mode: request.mode })
      const greeting = buildMockGreeting(conversation.id)
      return { success: true, data: { conversation, messages: [greeting], greeting } }
    },
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.startCoachConversation(request)
    }
  )

export const sendCoachMessage = async (request: SendCoachMessageRequest): Promise<SendCoachMessageResponse> =>
  handleRequest<SendCoachMessageResponse>(
    async () => ({ success: true, data: { reply: buildMockReply(request.conversationId) } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.sendCoachMessage(request)
    }
  )

export const appendCoachVoiceTurns = async (
  request: AppendCoachVoiceTurnsRequest
): Promise<AppendCoachVoiceTurnsResponse> =>
  handleRequest<AppendCoachVoiceTurnsResponse>(
    async () => ({ success: true, data: { conversation: buildMockConversation(request.conversationId) } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.appendCoachVoiceTurns(request)
    }
  )

export const appendAssistantTurns = async (
  request: AppendAssistantTurnsRequest
): Promise<AppendAssistantTurnsResponse> =>
  handleRequest<AppendAssistantTurnsResponse>(
    async () => ({ success: true, data: { conversationId: request.conversationId ?? createUniqueId() } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.appendAssistantTurns(request)
    }
  )

export const endCoachConversation = async (
  request: EndCoachConversationRequest
): Promise<EndCoachConversationResponse> =>
  handleRequest<EndCoachConversationResponse>(
    async () => ({ success: true, data: { conversation: buildMockEndedConversation(request.conversationId) } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.endCoachConversation(request)
    }
  )

export const getUserGoals = async (request: GetUserGoalsRequest): Promise<GetUserGoalsResponse> =>
  handleRequest<GetUserGoalsResponse>(
    async () => ({ success: true, data: { goals: MOCK_COACH_GOALS } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getUserGoals(request)
    }
  )

export const saveUserGoals = async (request: SaveUserGoalsRequest): Promise<SaveUserGoalsResponse> =>
  handleRequest<SaveUserGoalsResponse>(
    async () => ({
      success: true,
      data: {
        onboardingCompleted: true,
        droppedForLimit: 0,
        savedCount: request.goals.length,
        goals: buildMockSavedGoals(request.goals, request.source || GoalSource.COACH_FLOW)
      }
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.saveUserGoals(request)
    }
  )

export const rememberForLater = async (request: RememberForLaterRequest): Promise<RememberForLaterResponse> =>
  handleRequest<RememberForLaterResponse>(
    async () => ({ success: true, data: { remembered: true } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.rememberForLater(request)
    }
  )

export const updateUserGoal = async (request: UpdateUserGoalRequest): Promise<UpdateUserGoalResponse> =>
  handleRequest<UpdateUserGoalResponse>(
    async () => ({ success: true, data: { goals: buildMockEditedGoals(request) } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.updateUserGoal(request)
    }
  )

export const deleteUserGoal = async (request: DeleteUserGoalRequest): Promise<DeleteUserGoalResponse> =>
  handleRequest<DeleteUserGoalResponse>(
    async () => ({ success: true, data: { goals: MOCK_COACH_GOALS.filter((goal) => goal.id !== request.goalId) } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.deleteUserGoal(request)
    }
  )

export const mintCoachLiveToken = async (request: MintCoachLiveTokenRequest): Promise<MintCoachLiveTokenResponse> =>
  handleRequest<MintCoachLiveTokenResponse>(
    // Paired with StubRealtimeTransport — the token is never sent anywhere.
    async () => ({ success: true, data: buildMockLiveTokenConfig() }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.mintCoachLiveToken(request)
    }
  )

export const requestGoalSetting = async (request: RequestGoalSettingRequest): Promise<RequestGoalSettingResponse> =>
  handleRequest<RequestGoalSettingResponse>(
    async () => ({ success: true, data: { ok: true } }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.requestGoalSetting(request)
    }
  )
